import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useObsWizard } from "@/features/obs/hooks/obs-wizard-context";
import type { ObsRole } from "@/types/obs";

/** Roles the operator maps, in the order they are asked about. */
const MAPPABLE: { role: ObsRole; label: string; hint: string }[] = [
  {
    role: "year",
    label: "السنة",
    hint: "مطلوب — كل رقم في التقرير مرتبط بسنة",
  },
  { role: "entity", label: "الجهة", hint: "الوزارة أو المؤسسة أو بند التفصيل" },
  { role: "category", label: "التصنيف الاقتصادي", hint: "اختياري" },
  { role: "type", label: "النوع", hint: "اختياري — يظهر كمُصفٍّ في الشاشة" },
  { role: "budget", label: "المصدق", hint: "🔴 تأكيدك مطلوب" },
  { role: "actual", label: "الفعلي", hint: "🔴 تأكيدك مطلوب" },
  {
    role: "rate",
    label: "نسبة التنفيذ",
    hint: "اختياري — تُحتسب تلقائيًا إن لم تُحدَّد",
  },
];

const CONFIDENCE_STYLE: Record<string, string> = {
  high: "text-emerald-600",
  medium: "text-amber-600",
  low: "text-red-600",
};
const CONFIDENCE_LABEL: Record<string, string> = {
  high: "مرجّح",
  medium: "يحتاج مراجعة",
  low: "غير مؤكد",
};

/** Step 3 — confirm what each column means. The one step nothing can automate. */
export function StepMapping() {
  const { draft, patch, sheet, pickSheet, proposalFor } = useObsWizard();
  const { profile, roles } = draft;

  if (!profile) return null;

  return (
    <div className="space-y-4">
      {profile.sheets.length > 1 && (
        <div className="space-y-1.5">
          <Label>الورقة</Label>
          <Select value={draft.sheetName} onValueChange={pickSheet}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {profile.sheets.map((s) => (
                <SelectItem key={s.name} value={s.name}>
                  {s.name} — {s.dataRows.toLocaleString("en-US")} صف
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {sheet?.wide && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-sm">
          {sheet.wide.note} هذا التنسيق غير مدعوم بعد — حوّل السنوات إلى عمود
          واحد ثم أعد الرفع.
        </div>
      )}

      {(sheet?.notes ?? []).map((note, i) => (
        <div
          key={i}
          className="rounded-lg border border-border bg-muted/30 p-3 text-sm text-muted-foreground"
        >
          {note}
        </div>
      ))}

      {sheet && !sheet.wide && (
        <div className="space-y-3">
          {MAPPABLE.map(({ role, label, hint }) => {
            const proposal = proposalFor(role);
            const value = roles[role] ?? "";
            return (
              <div key={role} className="rounded-lg border border-border p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Label className="font-semibold">{label}</Label>
                  {proposal && (
                    <span
                      className={`text-xs ${CONFIDENCE_STYLE[proposal.confidence]}`}
                    >
                      {CONFIDENCE_LABEL[proposal.confidence]}
                    </span>
                  )}
                </div>
                <p className="mb-2 text-xs text-muted-foreground">
                  {proposal?.why ?? hint}
                </p>
                <Select
                  value={value || "__none__"}
                  onValueChange={(v) =>
                    patch({
                      roles: {
                        ...roles,
                        [role]: v === "__none__" ? undefined : v,
                      },
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="بدون" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">بدون</SelectItem>
                    {sheet.columns
                      .filter((c) => c.header)
                      .map((c) => (
                        <SelectItem key={c.index} value={c.header}>
                          {c.header}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {/* Real values from the file: the only way an operator can tell
                    two similarly-named columns apart. */}
                {value && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    أمثلة:{" "}
                    <span dir="auto">
                      {(
                        sheet.columns.find((c) => c.header === value)?.samples ??
                        []
                      )
                        .slice(0, 3)
                        .join(" · ") || "—"}
                    </span>
                  </p>
                )}
              </div>
            );
          })}

          <div className="flex items-start gap-3 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-sm">
            <Checkbox
              id="obs-measures-confirmed"
              checked={draft.measuresConfirmed}
              onCheckedChange={(v) => patch({ measuresConfirmed: v === true })}
              className="mt-0.5"
            />
            <Label
              htmlFor="obs-measures-confirmed"
              className="cursor-pointer font-normal leading-relaxed"
            >
              أؤكد أن عمود «المصدق» وعمود «الفعلي» صحيحان.
              <span className="block text-xs text-muted-foreground">
                لا يمكن للنظام تمييزهما من الملف، وعكسهما يقلب كل نسب التنفيذ على
                الموقع دون أن يبدو شيء مكسورًا.
              </span>
            </Label>
          </div>
        </div>
      )}
    </div>
  );
}
